import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Navbar.css";
import logo from "../../assets/logo.png";

const NAV_ITEMS = [
  { id: "home", label: "Home" },
  { id: "projects", label: "Projects" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

// ✅ smooth scroll that accounts for sticky header height
function scrollToSection(id, headerOffset = 0) {
  const el = document.getElementById(id);
  if (!el) return false;

  const y =
    window.scrollY + el.getBoundingClientRect().top - Math.max(8, headerOffset);

  window.scrollTo({ top: y, behavior: "smooth" });
  return true;
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ set this to your All Projects route
  const IS_PROJECTS_ALL = location.pathname === "/projects"; // change if needed

  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("projects");
  const [mobileOpen, setMobileOpen] = useState(false);

  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(72);

  const ids = useMemo(() => NAV_ITEMS.map((x) => x.id), []);

  // ✅ prevents hash-scroll from firing again when header height changes
  const handledHashRef = useRef(null);

  // close menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ✅ measure header height continuously (font loads / resize / scrolled style)
  useEffect(() => {
    const measure = () => {
      const h = headerRef.current?.getBoundingClientRect?.().height;
      if (h) setHeaderH(Math.ceil(h));
    };
    measure();

    window.addEventListener("resize", measure, { passive: true });

    const ro = new ResizeObserver(measure);
    if (headerRef.current) ro.observe(headerRef.current);

    return () => {
      window.removeEventListener("resize", measure);
      ro.disconnect();
    };
  }, []);

  // close on Escape + lock body scroll when menu open
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };

    if (mobileOpen) {
      document.addEventListener("keydown", onKeyDown);
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", onKeyDown);
        document.body.style.overflow = prev;
      };
    }
  }, [mobileOpen]);

  // ✅ Handle "/#contact" ONCE, then clear hash in router state so it never pulls you back.
  useEffect(() => {
    const hash = (location.hash || "").replace("#", "");
    if (!hash) return;

    const key = `${location.pathname}#${hash}`;
    if (handledHashRef.current === key) return;
    handledHashRef.current = key;

    const t = setTimeout(() => {
      const ok = scrollToSection(hash, headerH);

      if (ok) {
        setTimeout(() => {
          navigate(location.pathname, { replace: true });
        }, 120);
      }
    }, 60);

    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [location.pathname, location.hash, navigate, headerH]);

  // ✅ active section tracking only on Home (not on All Projects page)
  useEffect(() => {
    if (IS_PROJECTS_ALL) return;

    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const candidates = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0)
          );

        const best = candidates[0];
        if (best?.target?.id) setActive(best.target.id);
      },
      {
        rootMargin: `-${Math.max(8, headerH + 6)}px 0px -70% 0px`,
        threshold: [0.08, 0.15, 0.25, 0.4, 0.6],
      }
    );

    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [ids, headerH, IS_PROJECTS_ALL]);

  const go = (id) => {
    setMobileOpen(false);
    scrollToSection(id, headerH);
  };

  const goTop = () => {
    setMobileOpen(false);
    const ok = scrollToSection("home", headerH);
    if (!ok) window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ✅ CTA behavior:
  // - On Home: smooth scroll to contact
  // - On All Projects: navigate to "/#contact" then effect scrolls once & clears hash
  const goContact = (e) => {
    e?.preventDefault?.();
    setMobileOpen(false);

    if (IS_PROJECTS_ALL) {
      navigate("/#contact");
      return;
    }

    go("contact");
  };

  return (
    <>
      <header ref={headerRef} className={`nav ${scrolled ? "nav--scrolled" : ""}`}>
        <div className="nav__shell">
          <div className="nav__inner">
            {IS_PROJECTS_ALL ? (
              <Link className="nav__brand" to="/" aria-label="Go to Home">
                <img className="nav__logo" src={logo} alt="Patrick Riachi logo" />
              </Link>
            ) : (
              <button className="nav__brand" onClick={goTop} aria-label="Go to top">
                <img className="nav__logo" src={logo} alt="Patrick Riachi logo" />
              </button>
            )}

            <nav className="nav__links" aria-label="Primary">
              {IS_PROJECTS_ALL ? (
                <>
                  <Link className="nav__link" to="/">
                    Home
                  </Link>
                  <Link className="nav__link is-active" to="/projects">
                    Projects
                  </Link>
                </>
              ) : (
                NAV_ITEMS.map((item) => (
                  <button
                    key={item.id}
                    className={`nav__link ${active === item.id ? "is-active" : ""}`}
                    onClick={() => go(item.id)}
                  >
                    {item.label}
                  </button>
                ))
              )}
            </nav>

            <div className="nav__actions">
              {/* Desktop CTA only */}
              <a className="nav__cta nav__cta--desktop" href="/" onClick={goContact}>
                Schedule a call
              </a>

              {/* Burger ALWAYS present in markup; CSS controls visibility */}
              <button
                className={`nav__burger ${mobileOpen ? "is-open" : ""}`}
                onClick={() => setMobileOpen((v) => !v)}
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileOpen}
                aria-controls="mobile-menu"
              >
                <span />
                <span />
                <span />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile panel */}
      <div className={`mobile ${mobileOpen ? "mobile--open" : ""}`}>
        <button
          className="mobile__backdrop"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu backdrop"
        />

        <div
          id="mobile-menu"
          className="mobile__panel"
          role="dialog"
          aria-label="Menu"
          style={{ top: `calc(${headerH}px + 10px)` }}
        >
          <div className="mobile__top">
            <img className="mobile__logo" src={logo} alt="Patrick Riachi logo" />
            <button
              className="mobile__close"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>

          {/* ✅ CTA is inside the links list */}
          <div className="mobile__links">
            {IS_PROJECTS_ALL ? (
              <>
                <Link className="mobile__link" to="/">
                  Home
                </Link>
                <Link className="mobile__link is-active" to="/projects">
                  Projects
                </Link>
              </>
            ) : (
              NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  className={`mobile__link ${active === item.id ? "is-active" : ""}`}
                  onClick={() => go(item.id)}
                >
                  {item.label}
                </button>
              ))
            )}

            <button className="mobile__link mobile__ctaLink" onClick={goContact}>
              Schedule a call
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
